import {
  AfterDeath,
  DispositiveProvisionRecipientKind,
  TestamentaryEntityKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

import {
  BdgMissingBeneficiaryTier,
  BdgPrimaryRecipient,
  getDeceasedClientIdsAtTrigger,
  getMissingBeneficiaryTier,
  getSurvivingSpouseIdAtTrigger,
  isPrimaryTierUnavailable,
} from './bdgActiveTier';
import { BdgResolverEntityInput, getBdgTriggerInfo } from './bdgTriggerInfo';
import {
  DispositionsDiagram_BeneficiaryDesignationGroupFragment,
  DispositionsDiagram_DispositionProvisionFirstDeathFragment,
  DispositionsDiagram_DispositionProvisionTerminalFragment,
} from './graphql/DispositionsDiagram.generated';

export interface ResolvedDispositions {
  firstDeath: DispositionsDiagram_DispositionProvisionTerminalFragment[];
  secondDeath: DispositionsDiagram_DispositionProvisionTerminalFragment[];
  /**
   * Set for BDG entities whose active tier resolved empty; re-messages the
   * diagram's not-fully-distributed warning as "Please specify a {tier}
   * beneficiary".
   */
  missingBeneficiaryTier?: BdgMissingBeneficiaryTier;
}

export interface ResolveDispositionsContext {
  firstGrantorDeathId: string;
  possiblePrimaryClients: { id: string }[];
  /**
   * Section the entity will occupy in the cascade. The cascade reads
   * `firstDeath` at `AfterDeath.None` and `secondDeath` at
   * `AfterDeath.First`; BDG entities route their single active tier into
   * whichever slot will be read at this placement.
   */
  placedAt: AfterDeath;
}

const EMPTY: DispositionsDiagram_DispositionProvisionTerminalFragment[] = [];

/** Unifies DP and BDG entities behind one `{firstDeath, secondDeath}` shape. */
export function resolveEntityNormalizedDispositions(
  rawEntity: BdgResolverEntityInput,
  ctx: ResolveDispositionsContext
): ResolvedDispositions | null {
  const subtype = rawEntity.subtype;
  if (!subtype) return null;

  if ('beneficiaryDesignations' in subtype) {
    return resolveBdgDispositions(
      rawEntity,
      subtype.beneficiaryDesignations ?? null,
      ctx
    );
  }

  const scenario = subtype.dispositionScenarios?.find(
    (s) => s.firstGrantorDeath?.id === ctx.firstGrantorDeathId
  );
  if (!scenario) return null;
  return {
    firstDeath: getNodes(
      scenario.dispositiveProvisionsTemplate?.dispositiveProvisions ??
        scenario.dispositiveProvisions
    ),
    secondDeath: getNodes(
      scenario.secondDeathDispositiveProvisionsTemplate
        ?.dispositiveProvisions ?? scenario.secondDeathDispositiveProvisions
    ),
  };
}

function resolveBdgDispositions(
  rawEntity: BdgResolverEntityInput,
  bdgs: {
    primary?: DispositionsDiagram_BeneficiaryDesignationGroupFragment | null;
    contingent?: DispositionsDiagram_BeneficiaryDesignationGroupFragment | null;
  } | null,
  ctx: ResolveDispositionsContext
): ResolvedDispositions | null {
  const triggerInfo = getBdgTriggerInfo(rawEntity, ctx.possiblePrimaryClients);
  if (!triggerInfo) return null;

  const primaryDPs = getNodes(bdgs?.primary?.dispositiveProvisions);
  const contingentDPs = getNodes(bdgs?.contingent?.dispositiveProvisions);

  const possiblePrimaryClientIds = ctx.possiblePrimaryClients.map((c) => c.id);
  const deceasedAtTrigger = getDeceasedClientIdsAtTrigger({
    firstGrantorId: ctx.firstGrantorDeathId,
    triggeringClientIds: triggerInfo.triggeringClientIds,
    isSurvivorship: triggerInfo.isSurvivorship,
    possiblePrimaryClientIds,
  });
  const survivingSpouseId = getSurvivingSpouseIdAtTrigger(
    possiblePrimaryClientIds,
    ctx.firstGrantorDeathId
  );
  // T1-6171: recipient look-through is scoped to retirement *source* accounts
  // only. Insurance-source BDGs keep prior always-available recipient
  // behavior, matching the backend gate.
  const sourceIsRetirement =
    rawEntity.subtype?.__typename === 'RetirementPersonalAccount';
  const primaryRecipients = primaryDPs.map<BdgPrimaryRecipient>((dp) => ({
    isSurvivingSpouseSentinel:
      dp.recipientKind === DispositiveProvisionRecipientKind.SurvivingSpouse,
    individualId: dp.individual?.id ?? null,
    lookThroughOwnerIds: sourceIsRetirement
      ? (getLookThroughOwnerIds(dp.entity) ??
        getTestamentaryLookThroughOwnerIds(dp.testamentaryEntity))
      : null,
  }));
  // Downstream BDGs (placedAt = First) render their recipients in the
  // cascade's second-death slot, where both primary clients are already
  // dead. A primary recipient who would be alive at the BDG trigger but
  // dead by the display section cannot be shown there, so the tier
  // check uses the display-section deceased set instead of the trigger
  // set. Root BDGs (placedAt = None) align trigger and display
  // sections, so they keep using `deceasedAtTrigger`.
  const deceasedForTierCheck =
    ctx.placedAt !== AfterDeath.None
      ? new Set(possiblePrimaryClientIds)
      : deceasedAtTrigger;
  const useContingent = isPrimaryTierUnavailable(
    primaryRecipients,
    deceasedForTierCheck,
    survivingSpouseId
  );
  const activeDPs = useContingent ? contingentDPs : primaryDPs;
  const missingBeneficiaryTier = getMissingBeneficiaryTier({
    hasPrimaryRecipient: primaryDPs.length > 0,
    hasContingentRecipient: contingentDPs.length > 0,
    useContingent,
  });

  // Downstream BDG entities are reached at AfterDeath.First; their single
  // active tier feeds the next cascade step via secondDeath.
  if (ctx.placedAt !== AfterDeath.None) {
    return {
      firstDeath: EMPTY,
      secondDeath: activeDPs,
      missingBeneficiaryTier,
    };
  }

  // Root BDG: the active tier renders after the *triggering* death, which
  // may be either the first or second death in the current scenario.
  const triggerFiresAtSecondDeath =
    triggerInfo.isSurvivorship ||
    !triggerInfo.triggeringClientIds.includes(ctx.firstGrantorDeathId);
  return triggerFiresAtSecondDeath
    ? { firstDeath: EMPTY, secondDeath: activeDPs, missingBeneficiaryTier }
    : { firstDeath: activeDPs, secondDeath: EMPTY, missingBeneficiaryTier };
}

/**
 * Owner client ids for a recipient entity we "look through" to its owner's
 * death (retirement, individual/direct-holdings, joint accounts — T1-6171).
 * Returns null for every other recipient kind, so those keep their
 * always-available behavior. Joint accounts return all owners; the tier check
 * treats the recipient as dead only once every one of them has died.
 */
function getLookThroughOwnerIds(
  entity: DispositionsDiagram_DispositionProvisionFirstDeathFragment['entity']
): string[] | null {
  const subtype = entity?.subtype;
  if (!subtype) return null;
  switch (subtype.__typename) {
    case 'RetirementPersonalAccount':
    case 'IndividualPersonalAccount': {
      const ownerId = subtype.owner?.individual?.id;
      return ownerId ? [ownerId] : null;
    }
    case 'JointPersonalAccount': {
      const ownerIds = (subtype.owners ?? [])
        .map((owner) => owner?.individual?.id)
        .filter((id): id is string => Boolean(id));
      return ownerIds.length > 0 ? ownerIds : null;
    }
    default:
      return null;
  }
}

/**
 * Owner client id for a testamentary-entity recipient we "look through" — only
 * INHERITED_RETIREMENT_ACCOUNT TEs, to their account owner (the surviving owner
 * the inherited account is for). Returns null for every other TE kind (and TEs
 * with no account owner), keeping their always-available behavior. (T1-6171)
 */
function getTestamentaryLookThroughOwnerIds(
  te: DispositionsDiagram_DispositionProvisionFirstDeathFragment['testamentaryEntity']
): string[] | null {
  if (
    te?.testamentaryEntityKind !==
    TestamentaryEntityKind.InheritedRetirementAccount
  ) {
    return null;
  }
  const ownerId = te.accountOwner?.id;
  return ownerId ? [ownerId] : null;
}
